import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Promocode, IPromoCodeType } from '../../resources/types/balanceTypes';
import PromocodeModal from '../../modals/PromocodeModal';
import PromoCodesListWrapper from './components/PromoCodesListWrapper';
import { Pagination, Box } from '@mui/material';
import { getPromocodesAsync, selectPromocodes } from '../../redux/slices/promocodesSlice';

const PREFIX = 'PromocodesPage';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`,
  tableContainer: `${PREFIX}-tableContainer`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center'
  },
  [`& .${classes.addButton}`]: {
    marginLeft: 20
  },
  [`& .${classes.tableContainer}`]: {
    marginTop: 40,
    marginBottom: 40
  }
});

const newPromocode: Promocode = {
  code: "", 
  type: IPromoCodeType.addBalance,
  amount: 0,
  expiresAt: "",
  description: "",
  usingLimit: 0,
  city: "",
  active: true,
};

const PromocodesPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();


  const { limit, total, items } = useSelector(selectPromocodes);
  const [page, setPage] = useState(1);
  const [includeReferal, setIncludeReferal] = useState(false);

  const paginationCount = useMemo(
    () => Math.ceil(total / limit),
    [total, limit]
  );

  const onChangePage = useCallback((e: any, nextPage: number) => {
    setPage(nextPage);
  }, []);

  const fetchPromocodes = (page: number, limit: number) => {
    dispatch(getPromocodesAsync({ page, limit, includeReferal }));
  }

  useEffect(() => {
    fetchPromocodes(page, limit)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, includeReferal]);

  const [open, setOpen] = useState(false);
  const [selectedPromocode, setSelectedPromocode] = useState<Promocode | null>(
    null
  );

  return (
    <Root>
      <PromoCodesListWrapper
        title={t('promocodes.title')}
        createButtonTitle={t('promocodes.createButtonTitle')}
        onCreate={() => {
          setSelectedPromocode(newPromocode);
          setOpen(true);
        }}
        onEdit={(promocode: Promocode) => {
          setOpen(true);
          setSelectedPromocode(promocode);
        }}
        promocodes={items ?? []}
        includeReferal={includeReferal}
        setIncludeReferal={setIncludeReferal}
      />
      {paginationCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={paginationCount} 
            page={page}
            onChange={onChangePage}
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 600,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
                  color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? '#212121' : '#E0E0E0'
                  }
                }
              }
            }}
          />
        </Box>
      )}
      <PromocodeModal
        selectedPromocode={selectedPromocode}
        open={open}
        setOpen={setOpen}
        setSelectedPromocode={setSelectedPromocode}
        onSuccess={() => fetchPromocodes(page, limit)}
      />
    </Root>
  );
};

export default PromocodesPage;
